import { NotificationPriority } from '@shared/types';
import { notificationSeverityStyles } from '@web-app/components/Notifications/notification-severity-styles';
import { Button } from '@web-app/components/ui/Button/button';
import { Notification } from '@web-app/hooks/queries/notifications';
import { cn } from '@web-app/lib/utils';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { IconType } from 'react-icons';
import { LuCircleAlert, LuCircleCheck, LuTriangleAlert } from 'react-icons/lu';

const priorityIcons: Record<NotificationPriority, IconType> = {
  [NotificationPriority.Info]: LuCircleCheck,
  [NotificationPriority.Warning]: LuTriangleAlert,
  [NotificationPriority.Critical]: LuCircleAlert,
};

interface NotificationBannerProps {
  notification: Notification;
}

export const NotificationBanner = ({
  notification,
}: NotificationBannerProps) => {
  const router = useRouter();
  const [isNavigating, setIsNavigating] = useState(false);

  const styles = notificationSeverityStyles[notification.priority];
  const NotificationIcon = priorityIcons[notification.priority];

  // Navigate to destination - markAsViewed is called by the destination page
  const handleLearnMore = async () => {
    setIsNavigating(true);
    await router.push(notification.route);
  };

  return (
    <div
      className={cn(
        'mb-4 w-full rounded-md border bg-white p-4 transition-shadow duration-200 ease-in-out hover:shadow-sm',
        styles.borderClassName,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <NotificationIcon
            className={cn('mt-0.5 size-6 shrink-0', styles.iconClassName)}
          />
          <div className="min-w-0">
            <p className="font-semibold text-foreground-legacy">
              {notification.title}
            </p>
            {notification.message && (
              <p className="mt-1 text-sm leading-relaxed text-foreground-legacy/70">
                {notification.message}
              </p>
            )}
          </div>
        </div>
        <Button
          size="sm"
          className={cn('shrink-0', styles.actionClassName)}
          onClick={handleLearnMore}
          disabled={isNavigating}
        >
          Learn more
        </Button>
      </div>
    </div>
  );
};
